import { createContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { formatDateWithTimezoneOffset } from '../../../reports/helpers/utils';
import { useFetchSubRComponentData } from '../../../../helpers/hooks/useFetchSubRComponentData';
import { createDecimalNumberFormatter } from '../../../../helpers/utils';
import { parseNumber } from './utils';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../state/store';

export const SubRDataContext = createContext(null);

export const SubRDataProvider = ({ children, selectedOrder }) => {
  const routeMatch = useParams();
  const formatDecimal = createDecimalNumberFormatter(2);
  const companyTimezoneOffset = useSelector(
    (state: RootState) => state?.company?.company_details?.time_zone_offset
  );

  const {
    order_date: orderDate,
    order_no,
    offset,
    order_type,
    location_id,
    staff,
    order_status,
    scheduled_date,
    currency
  } = selectedOrder;

  const order_id = useMemo(() => {
    const row = selectedOrder as Record<string, unknown>;
    const raw = row?.order_id ?? row?.orderId;
    if (raw === undefined || raw === null || raw === '') return undefined;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [selectedOrder]);
  const order_date = orderDate || scheduled_date;
  const is_tax_inclusive = selectedOrder?.is_tax_inclusive ?? false;
  const timezoneOffset = useMemo(() => {
    const fallbackOffset = companyTimezoneOffset || offset || '+00:00';
    return /^([+-])(\d{2}):(\d{2})$/.test(fallbackOffset) ? fallbackOffset : '+00:00';
  }, [companyTimezoneOffset, offset]);
  const date = formatDateWithTimezoneOffset(order_date, timezoneOffset, 'yyyy-MM-dd');
  const showHistoryTab = useMemo(() => {
    if (!order_date) {
      return true;
    }
    const orderDateInCompanyTimezone = formatDateWithTimezoneOffset(
      order_date,
      timezoneOffset,
      'yyyy-MM-dd'
    );
    return orderDateInCompanyTimezone < '2026-01-01';
  }, [order_date, timezoneOffset]);

  const apiEndpoint = useMemo(() => {
    const { dashboard, widget } = routeMatch;
    if (order_id === undefined || !dashboard || !widget) {
      return {
        orderItems: null,
        orderPayments: null,
        orderDiscounts: null,
        orderSurcharges: null,
        orderTimeline: null,
        orderHistory: null,
        modification: null,
        auditLogs: null
      };
    }
    const base = `/api/v1/dar/${dashboard}/${widget}/sub-r/${order_id}`;
    const qs = `order_date=${order_date}&location=${location_id}`;
    return {
      orderItems: `${base}/order-items?${qs}`,
      orderPayments: `${base}/order-payments?${qs}`,
      orderDiscounts: `${base}/order-discounts?${qs}`,
      orderSurcharges: `${base}/order-surcharges?${qs}`,
      orderTimeline: `${base}/order-timeline?${qs}`,
      orderHistory: `${base}/order-history?${qs}`,
      modification: `${base}/modifications?${qs}`,
      auditLogs: `${base}/audit-logs?${qs}`
    };
  }, [routeMatch, order_id, order_date, location_id]);

  const {
    data: itemsData,
    error: itemsDataError,
    isValidating: isItemsDataLoading,
    mutate: reloadItemsData
  } = useFetchSubRComponentData(apiEndpoint.orderItems);

  const {
    data: paymentsData,
    error: paymentsDataError,
    isValidating: isPaymentsDataLoading,
    mutate: reloadPaymentsData
  } = useFetchSubRComponentData(apiEndpoint.orderPayments);

  const {
    data: discountsData,
    error: discountDataError,
    isValidating: isDiscountsDataLoading,
    mutate: reloadDiscountData
  } = useFetchSubRComponentData(apiEndpoint.orderDiscounts);

  const {
    data: surchargeData,
    error: surchargeError,
    isValidating: isSurchargeDataLoading,
    mutate: reloadSurchargeData
  } = useFetchSubRComponentData(apiEndpoint.orderSurcharges);

  const {
    data: timelineData,
    error: timelineDataError,
    isValidating: isTimelineDataLoading,
    mutate: reloadTimelineData
  } = useFetchSubRComponentData(apiEndpoint.orderTimeline);

  const {
    data: orderHistory,
    error: orderHistoryDataError,
    isValidating: isOrderHistoryDataLoading,
    mutate: reloadOrderHistoryData
  } = useFetchSubRComponentData(apiEndpoint.orderHistory);

  const {
    data: modification,
    error: modificationDataError,
    isValidating: isModificationDataLoading,
    mutate: reloadModificationData
  } = useFetchSubRComponentData(apiEndpoint.modification);

  const {
    data: auditLogs,
    error: auditLogsDataError,
    isValidating: isAuditLogsDataLoading,
    mutate: reloadAuditLogsData
  } = useFetchSubRComponentData(apiEndpoint.auditLogs);

  const isOrderSummaryDataLoading = useMemo(() => {
    let isOrderItemsDataLoading = !itemsData && isItemsDataLoading;
    let isDiscountsLoading = !discountsData && isDiscountsDataLoading;
    let isSurchargeLoading = !surchargeData && isSurchargeDataLoading;
    let isPaymentsLoading = !paymentsData && isPaymentsDataLoading;
    let isOrderHistoryLoading = !orderHistory && isOrderHistoryDataLoading;
    let isModificationLoading = !modification && isModificationDataLoading;
    let isTimelineLoading = !timelineData && isTimelineDataLoading;
    return (
      isOrderItemsDataLoading ||
      isDiscountsLoading ||
      isSurchargeLoading ||
      isPaymentsLoading ||
      isOrderHistoryLoading ||
      isModificationLoading ||
      isTimelineLoading
    );
  }, [
    itemsData,
    isItemsDataLoading,
    discountsData,
    isDiscountsDataLoading,
    surchargeData,
    isSurchargeDataLoading,
    paymentsData,
    isPaymentsDataLoading,
    orderHistory,
    isOrderHistoryDataLoading,
    modification,
    isModificationDataLoading,
    timelineData,
    isTimelineDataLoading
  ]);

  const isOrderSummaryDataLoadError = useMemo(() => {
    return (
      itemsDataError ||
      discountDataError ||
      surchargeError ||
      paymentsDataError ||
      orderHistoryDataError ||
      modificationDataError ||
      timelineDataError
    );
  }, [
    itemsDataError,
    discountDataError,
    surchargeError,
    paymentsDataError,
    orderHistoryDataError,
    modificationDataError,
    timelineDataError
  ]);

  const orderAmountBreakdowns = useMemo(() => {
    let subTotal = 0;
    let discountTotal = 0;
    let surchargeTotal = 0;
    let totalAmount = 0;
    let taxTotal = 0;
    if (itemsData) {
      subTotal = formatDecimal(
        itemsData.reduce((total, d) => {
          return total + parseNumber(d.item_amount);
        }, 0)
      );
      if (is_tax_inclusive) {
        taxTotal = 0;
      } else {
        taxTotal = formatDecimal(
          itemsData.reduce((total, d) => {
            return total + parseNumber(d.item_tax);
          }, 0) + parseNumber(itemsData[0]?.surcharge_tax || 0)
        );
      }
    }
    if (discountsData) {
      discountTotal = formatDecimal(
        discountsData.reduce((total, d) => {
          return total + parseNumber(d.total_discounts);
        }, 0)
      );
    }
    if (surchargeData) {
      surchargeTotal = formatDecimal(
        surchargeData.reduce((total, d) => {
          return total + parseNumber(d.surcharge_amount);
        }, 0)
      );
    }
    if (paymentsData) {
      totalAmount = formatDecimal(
        parseNumber(subTotal) +
          parseNumber(taxTotal) -
          parseNumber(discountTotal) +
          parseNumber(surchargeTotal)
      );
    }
    return { subTotal, discountTotal, surchargeTotal, taxTotal, totalAmount };
  }, [
    itemsData,
    isItemsDataLoading,
    discountsData,
    isDiscountsDataLoading,
    paymentsData,
    isPaymentsDataLoading,
    surchargeData,
    isSurchargeDataLoading
  ]);

  const popupData = useMemo(() => {
    type PopupData = { name: string; value: number }[];
    let discountsDetails: PopupData = [];
    let surchargeDetails: PopupData = [];
    if (discountsData) {
      discountsDetails = discountsData.map((d) => ({
        name: d.discount,
        value: d.total_discounts
      }));
    }
    if (surchargeData) {
      surchargeDetails = surchargeData.map((d) => ({
        name: d.surcharge,
        value: d.surcharge_amount
      }));
    }
    return { discountsDetails, surchargeDetails };
  }, [discountsData, surchargeData]);

  const reloadData = () => {
    reloadItemsData();
    reloadPaymentsData();
    reloadDiscountData();
    reloadSurchargeData();
    reloadOrderHistoryData();
    reloadModificationData();
    reloadTimelineData();
    reloadAuditLogsData();
  };

  const { customer, customer_contact, table_no, cover } = useMemo(() => {
    let customer = null;
    let customer_contact = null;
    let table_no = null;
    let cover = null;
    const firstItem = Array.isArray(itemsData) ? itemsData[0] : null;
    if (firstItem) {
      customer = firstItem.customer_name || null;
      customer_contact = firstItem.customer_phone || null;
      table_no = firstItem.table_no || null;
      cover = firstItem.covers || null;
    }
    return { customer, customer_contact, table_no, cover };
  }, [itemsData]);

  const providerData: any = useMemo(() => {
    return {
      itemsData,
      paymentsData,
      discountsData,
      surchargeData,
      timelineData,
      orderAmountBreakdowns,
      popupData,
      isOrderSummaryDataLoading,
      isOrderSummaryDataLoadError,
      reloadData,
      orderHistory,
      modification,
      auditLogs,
      auditLogsDataError,
      isAuditLogsDataLoading,
      order_no,
      order_type,
      order_status,
      staff,
      date: formatDateWithTimezoneOffset(order_date, timezoneOffset, 'dd-LLL-yyyy pp'),
      table_no,
      cover,
      customer,
      customer_contact,
      currency,
      showHistoryTab,
      timezoneOffset
    };
  }, [
    paymentsData,
    isPaymentsDataLoading,
    discountsData,
    itemsData,
    surchargeData,
    timelineData,
    orderAmountBreakdowns,
    popupData,
    isOrderSummaryDataLoading,
    isOrderSummaryDataLoadError,
    orderHistory,
    modification,
    order_no,
    order_type,
    staff,
    order_date,
    currency,
    showHistoryTab,
    timezoneOffset,
    auditLogs,
    auditLogsDataError,
    isAuditLogsDataLoading
  ]);

  return <SubRDataContext.Provider value={providerData}>{children}</SubRDataContext.Provider>;
};
